// OneTimeIncome.js (обновленная версия с фильтром по умолчанию)
import {FEM_V, instance_2} from "../../../API/API";
import {useCallback, useEffect, useMemo, useState} from "react";
import {Button, DatePicker, message, Select, Space, Table, Tag} from "antd";
import {DeleteOutlined, EditOutlined, PlusOutlined} from "@ant-design/icons";
import dayjs from "dayjs";

import {useDebounce} from "./hooks/useDebounce";
import {SearchHeader} from "./subcomponents/income_periodic/SearchHeader";
import EditModal from "./subcomponents/income_one/EditModal";
import DeleteConfirmModal from "./subcomponents/income_one/DeleteConfirmModal";
import TableSummary from "./subcomponents/income_one/TableSummary";
import {ObjectNameCell} from "./subcomponents/income_periodic/ObjectNameCell";
import ExpensesHeader from "./subcomponents/expenses_header/ExpensesHeader";

const {RangePicker} = DatePicker;
const {Option} = Select;

const OneTimeIncome = () => {
    const [loading, setLoading] = useState(true);
    const [incomeData, setIncomeData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState(() => {
        // Устанавливаем фильтр на текущий месяц по умолчанию
        const now = dayjs();
        const startOfMonth = now.startOf('month');
        const endOfMonth = now.endOf('month');
        return [startOfMonth, endOfMonth];
    });
    const [typeFilter, setTypeFilter] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [currentRecord, setCurrentRecord] = useState(null);
    const [deleteRecord, setDeleteRecord] = useState(null);

    const debouncedSearchText = useDebounce(searchText, 300);

    const getOneTimeIncome = useCallback(async (filters = {}) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();

            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.type !== undefined && filters.type !== null) params.append('type', filters.type);

            const queryString = params.toString();
            const url = `finance/getOneIncome.php${queryString ? `?${queryString}` : ''}`;

            const response = await instance_2.get(url);

            // Проверяем структуру ответа
            console.log('API Response:', response.data);

            // Данные могут быть в разных местах в зависимости от ответа
            let data = [];
            if (response.data && response.data.one_income) {
                data = response.data.one_income;
            } else if (Array.isArray(response.data)) {
                data = response.data;
            } else if (response.data && response.data.data) {
                data = response.data.data;
            }

            // Фильтруем записи с пустыми названиями и сортируем по дате
            const filteredData = data.filter(item => item.name && item.name.trim() !== '');
            const sortedData = filteredData.sort((a, b) => (b.date || 0) - (a.date || 0));

            console.log('Processed data:', sortedData);
            setIncomeData(sortedData);
        } catch (error) {
            console.error('Error fetching one-time income:', error);
            message.error('Ошибка при загрузке данных');
            setIncomeData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const filters = {};
        if (dateRange && dateRange[0] && dateRange[1]) {
            filters.date_from = dateRange[0].format('YYYY-MM-DD');
            filters.date_to = dateRange[1].format('YYYY-MM-DD');
        }
        if (typeFilter !== null && typeFilter !== undefined) {
            filters.type = typeFilter;
        }
        getOneTimeIncome(filters);
    }, [dateRange, typeFilter, getOneTimeIncome]);

    const handleAdd = useCallback(() => {
        setCurrentRecord(null);
        setIsModalVisible(true);
    }, []);

    const handleEdit = useCallback((record) => {
        setCurrentRecord(record);
        setIsModalVisible(true);
    }, []);

    const handleDelete = useCallback((record) => {
        setDeleteRecord(record);
        setIsDeleteModalVisible(true);
    }, []);

    const handleModalClose = useCallback(() => {
        setIsModalVisible(false);
        setCurrentRecord(null);
    }, []);

    const handleDeleteModalClose = useCallback(() => {
        setIsDeleteModalVisible(false);
        setDeleteRecord(null);
    }, []);

    const handleSave = async (formData) => {
        try {
            const params = new URLSearchParams();
            params.append('name', formData.name);
            params.append('type', formData.type);
            params.append('cost', formData.cost);
            params.append('nds', formData.nds);
            params.append('date', formData.date);
            params.append('update', formData.id ? 1 : 0);
            if (formData.id) {
                params.append('id', formData.id);
            }

            const response = await instance_2.post('finance/postOneIncome.php', params);
            console.log('Save response:', response.data);

            message.success(formData.id ? 'Запись успешно обновлена' : 'Запись успешно создана');

            handleModalClose();
            const filters = {};
            if (dateRange && dateRange[0] && dateRange[1]) {
                filters.date_from = dateRange[0].format('YYYY-MM-DD');
                filters.date_to = dateRange[1].format('YYYY-MM-DD');
            }
            if (typeFilter !== null) filters.type = typeFilter;
            getOneTimeIncome(filters);
        } catch (error) {
            console.error('Error saving data:', error);
            message.error('Ошибка при сохранении данных');
        }
    };

    const handleConfirmDelete = async () => {
        if (!deleteRecord) return;

        try {
            const params = new URLSearchParams();
            params.append('id', deleteRecord.id);
            params.append('delete', 1);

            const response = await instance_2.post('finance/deleteOneIncome.php', params);
            console.log('Delete response:', response.data);

            message.success('Запись успешно удалена');

            handleDeleteModalClose();
            const filters = {};
            if (dateRange && dateRange[0] && dateRange[1]) {
                filters.date_from = dateRange[0].format('YYYY-MM-DD');
                filters.date_to = dateRange[1].format('YYYY-MM-DD');
            }
            if (typeFilter !== null) filters.type = typeFilter;
            getOneTimeIncome(filters);
        } catch (error) {
            console.error('Error deleting data:', error);
            message.error('Ошибка при удалении записи');
        }
    };

    const filteredData = useMemo(() => {
        if (!debouncedSearchText) return incomeData;

        return incomeData.filter(item =>
            item.name?.toLowerCase().includes(debouncedSearchText.toLowerCase())
        );
    }, [incomeData, debouncedSearchText]);

    const getTotalWithNds = (cost, nds) => {
        if (nds === 0) return cost;
        return cost * (1 + nds / 100);
    };

    const formatDate = (timestamp) => {
        if (!timestamp || timestamp === 0) return '—';
        try {
            const date = new Date(timestamp * 1000);
            return date.toLocaleDateString('ru-RU');
        } catch (e) {
            return '—';
        }
    };

    const columns = useMemo(() => [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 30,
            align: 'center',
            render: (id) => (
                <span style={{
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    color: '#595959',
                    fontWeight: 400
                }}>
                    {id}
                </span>
            ),
        },
        {
            title: () => <SearchHeader value={searchText} onChange={setSearchText}
                                       placeholder="Поиск по наименованию"/>,
            dataIndex: 'name',
            key: 'name',
            width: 150,
            fixed: 'left',
            render: (text, record) => <ObjectNameCell text={text} balance={record.balance}/>,
        },
        {
            title: 'Форма оплаты',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            filters: [
                {text: 'Наличная', value: 1},
                {text: 'Безналичная', value: 2},
            ],
            onFilter: (value, record) => record.type === value,
            render: (type) => {
                return (
                    <span style={{
                        fontSize: '13px',
                        color: type === 1 ? '#52c41a' : '#1890ff',
                        fontWeight: 500
                    }}>
                        {type === 1 ? 'Наличная' : 'Безналичная'}
                     </span>
                )
            },
        },
        {
            title: 'Дата',
            dataIndex: 'date',
            key: 'date',
            width: 100,
            align: 'center',
            sorter: (a, b) => (a.date || 0) - (b.date || 0),
            render: (timestamp) => formatDate(timestamp),
        },
        {
            title: 'Сумма',
            dataIndex: 'cost',
            key: 'cost',
            width: 100,
            align: 'right',
            sorter: (a, b) => (a.cost || 0) - (b.cost || 0),
            render: (cost, record) => {
                const total = getTotalWithNds(record.cost, record.nds);

                return (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: 'fit-content',
                        fontFamily: 'monospace',
                        margin: '0 auto',
                        borderRadius: '30px', // Скругленные пилюли
                    }}>
                        {/* Без НДС */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            fontSize: '12px',
                            color: '#6b6b6b',
                            borderBottom: '1px dashed #c9c9c9',
                            paddingBottom: '3px',
                            marginBottom: '3px'
                        }}>
                            <span>Без НДС</span>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#2f5e8c',
                            }}>
                                    €{cost.toFixed(2)}
                                </span>
                        </div>

                        {/* С НДС */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '12px',
                            fontSize: '12px',
                            color: '#6b6b6b',
                            borderBottom: '1px dashed #c9c9c9',
                            paddingBottom: '3px',
                            marginBottom: '3px'
                        }}>
                            <span>С НДС</span>
                            <span style={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#1f7b4c',
                                letterSpacing: '-0.5px'
                            }}>
                            €{total.toFixed(2)}
                            </span>
                        </div>
                    </div>

                )
            }
        },

        {
            title: 'НДС, %',
            dataIndex: 'nds',
            key: 'nds',
            width: 80,
            align: 'center',
            filters: [
                {text: 'Без НДС', value: 0},
                {text: 'НДС 21%', value: 21},
            ],
            onFilter: (value, record) => record.nds === value,
            render: (nds) => (
                nds > 0 ? (
                    <span style={{
                        fontSize: '13px',
                        color: '#fa541c',
                        backgroundColor: '#fff2e8',
                        padding: '2px 6px',
                        borderRadius: '2px'
                    }}>
            {nds}%
        </span>
                ) : (
                    <span style={{fontSize: '13px', color: '#bfbfbf'}}>—</span>
                )
            ),
        },
        {
            title: 'Действия',
            key: 'actions',
            width: 100,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined/>}
                        onClick={() => handleEdit(record)}
                        title="Редактировать"
                    />
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined/>}
                        onClick={() => handleDelete(record)}
                        title="Удалить"
                    />
                </Space>
            ),
        },
    ], [searchText, handleEdit, handleDelete]);

    // Для отладки
    console.log('Rendering with data:', filteredData);

    return (
        <div>
            <div style={{margin: '0px auto 10px auto'}}>
                <ExpensesHeader module_v={FEM_V} header_text={'Управление разовыми доходами компании'}/>
            </div>
            <div style={{width: '100%', textAlign: 'center', margin: '20px auto'}}>
                <Space size="middle" wrap>
                    <RangePicker
                        format="YYYY-MM-DD"
                        value={dateRange}
                        onChange={setDateRange}
                        placeholder={['Дата с', 'Дата по']}
                    />
                </Space>

                <Button
                    type="link"
                    icon={<PlusOutlined/>}
                    onClick={handleAdd}
                >
                    Добавить доход
                </Button>
            </div>

            <Table
                dataSource={filteredData}
                loading={loading}
                columns={columns}
                rowKey="id"
                pagination={false}
                scroll={{x: 1300, y: 600}}
                bordered
                size="small"
                summary={(pageData) => <TableSummary pageData={pageData}/>}
                sticky
                locale={{
                    emptyText: 'Нет данных'
                }}
                title={() => (
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                        <span style={{fontSize: '16px', fontWeight: 500}}>
                            Разовые доходы
                        </span>
                        <Tag color="blue">Всего записей: {filteredData?.length || 0}</Tag>
                    </div>
                )}
            />

            <EditModal
                visible={isModalVisible}
                record={currentRecord}
                onClose={handleModalClose}
                onSave={handleSave}
            />

            <DeleteConfirmModal
                visible={isDeleteModalVisible}
                record={deleteRecord}
                onClose={handleDeleteModalClose}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};

export default OneTimeIncome;